import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { logDev } from "@/lib/log";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const UnreadContext = createContext(null);

const LS_KEY = "ts_unread_v1";

function loadInitial() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (err) { logDev(err); }
  return { announcement: 0, signal: 0, dm: 0 };
}

/**
 * Tracks unread counters per navigation section so BottomNav can render badges.
 * Counters are bumped by NotificationStream on SSE events and cleared when the
 * user opens the matching page. DM unread is polled from the backend since
 * admin replies don't currently emit an SSE event to the user.
 */
export function UnreadProvider({ children }) {
  const [counts, setCounts] = useState(loadInitial);
  const location = useLocation();
  const { user } = useAuth();
  const isApprovedUser = user && user.role !== "admin" && user.status === "approved";

  // Persist across reloads so a signal that arrived while the tab was closed
  // still shows as unread when the user comes back.
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(counts)); } catch (err) { logDev(err); }
  }, [counts]);

  // Auto-clear when the user opens the matching route.
  useEffect(() => {
    if (location.pathname === "/") setCounts((c) => (c.announcement ? { ...c, announcement: 0 } : c));
    if (location.pathname === "/performance") setCounts((c) => (c.signal ? { ...c, signal: 0 } : c));
    if (location.pathname === "/profile") setCounts((c) => (c.dm ? { ...c, dm: 0 } : c));
  }, [location.pathname]);

  // Poll DM unread count for approved (non-admin) users only.
  useEffect(() => {
    if (!isApprovedUser) return;
    let cancelled = false;
    const fetchDmCount = async () => {
      try {
        const [{ data: admin }, { data: peer }] = await Promise.all([
          api.get("/dm/unread-count"),
          api.get("/user-dm-unread-count"),
        ]);
        if (cancelled) return;
        const n = (admin.count || 0) + (peer.count || 0);
        setCounts((c) => (c.dm === n ? c : { ...c, dm: n }));
      } catch (err) { /* silent — transient */ }
    };
    fetchDmCount();
    const t = setInterval(fetchDmCount, 20000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isApprovedUser]);

  const bump = useCallback((kind) => {
    setCounts((c) => ({ ...c, [kind]: (c[kind] || 0) + 1 }));
  }, []);

  const clear = useCallback((kind) => {
    setCounts((c) => ({ ...c, [kind]: 0 }));
  }, []);

  const value = useMemo(() => ({ counts, bump, clear }), [counts, bump, clear]);
  return <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>;
}

export function useUnread() {
  return useContext(UnreadContext);
}
